import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { X, Upload, Trash2, FileText, Video, Image, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';


// Allowed file types
const ALLOWED_TYPES = {
  'image/jpeg': { icon: Image, label: 'Image' },
  'image/png': { icon: Image, label: 'Image' },
  'image/gif': { icon: Image, label: 'Image' },
  'image/webp': { icon: Image, label: 'Image' },
  'application/pdf': { icon: FileText, label: 'PDF' },
  'video/mp4': { icon: Video, label: 'Video' },
  'video/webm': { icon: Video, label: 'Video' },
  'video/quicktime': { icon: Video, label: 'Video' },
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB for videos

function CreateTicketModal({
  boards,
  defaultBoardId,
  defaultColumnId,
  customFields = [],
  onClose,
  onTicketCreated,
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    description_images: [],
    priority: 'medium',
    board_id: defaultBoardId || '',
    column_id: defaultColumnId || '',
    assignee_user_id: '',
    due_date: '',
    custom_fields: {},
  });
  const [columns, setColumns] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  const filteredUsers = useMemo(() => {
    if (!assigneeSearch.trim()) return users;
    const q = assigneeSearch.toLowerCase();
    return users.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [users, assigneeSearch]);

  useEffect(() => {
    loadUsers();
    if (formData.board_id) {
      loadColumns(formData.board_id);
    }
  }, []);

  useEffect(() => {
    if (formData.board_id) {
      loadColumns(formData.board_id);
    }
  }, [formData.board_id]);

  const loadUsers = async () => {
    try {
      const response = await api.get(`/employees/active`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadColumns = async (boardId) => {
    try {
      const response = await api.get(`/boards/${boardId}/columns`, {
        withCredentials: true,
      });
      setColumns(response.data);
      if (!formData.column_id && response.data.length > 0) {
        setFormData((prev) => ({ ...prev, column_id: response.data[0].column_id }));
      }
    } catch (error) {
      console.error('Failed to load columns:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    const uploadedIds = [];
    const newFileMetadata = [];

    for (const file of files) {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Max size is 50MB`);
        continue;
      }

      // Check file type
      if (!ALLOWED_TYPES[file.type]) {
        toast.error(`${file.name}: Only images, PDFs, and videos are allowed`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('ticket_id', 'temp');

        const response = await api.post(`/attachments`, formDataUpload, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        uploadedIds.push(response.data.attachment_id);
        newFileMetadata.push({
          id: response.data.attachment_id,
          name: file.name,
          type: file.type,
        });
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (uploadedIds.length > 0) {
      setFormData((prev) => ({
        ...prev,
        description_images: [...prev.description_images, ...uploadedIds],
      }));
      setUploadedFiles((prev) => [...prev, ...newFileMetadata]);
      toast.success(`${uploadedIds.length} file(s) uploaded`);
    }

    setUploadingFiles(false);
    e.target.value = '';
  };

  const removeFile = (attachmentId) => {
    setFormData((prev) => ({
      ...prev,
      description_images: prev.description_images.filter((id) => id !== attachmentId),
    }));
    setUploadedFiles((prev) => prev.filter((f) => f.id !== attachmentId));
  };

  const getFileIcon = (type) => {
    const config = ALLOWED_TYPES[type];
    return config ? config.icon : FileText;
  };

  const getFileLabel = (type) => {
    const config = ALLOWED_TYPES[type];
    return config ? config.label : 'File';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Guard: prevent submit while files are still uploading
    // Without this, formData.description_images is empty at submit time and attachments are orphaned
    if (uploadingFiles) {
      toast.error('Please wait for files to finish uploading');
      return;
    }
    
    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.board_id) {
      toast.error('Please select a board');
      return;
    }
    if (!formData.column_id) {
      toast.error('Please select a column');
      return;
    }
    if (!formData.assignee_user_id) {
      toast.error('Assignee is required');
      return;
    }
    if (!formData.due_date) {
      toast.error('Due date is required');
      return;
    }
    
    // Validate mandatory custom fields
    for (const field of customFields) {
      if (field.is_mandatory) {
        const values = formData.custom_fields[field.field_id] || [];
        if (values.length === 0) {
          toast.error(`${field.name} is required`);
          return;
        }
      }
    }

    setLoading(true);
    try {
      const response = await api.post(`/tickets`, formData);
      toast.success('Ticket created successfully');
      onTicketCreated(response.data);
    } catch (error) {
      console.error('Failed to create ticket:', error);
      toast.error('Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomFieldChange = (fieldId, value, checked) => {
    setFormData(prev => {
      const currentValues = prev.custom_fields[fieldId] || [];
      let newValues;
      if (checked) {
        newValues = [...currentValues, value];
      } else {
        newValues = currentValues.filter(v => v !== value);
      }
      return {
        ...prev,
        custom_fields: {
          ...prev.custom_fields,
          [fieldId]: newValues
        }
      };
    });
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full my-8 border border-stone-200" data-testid="create-ticket-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h2 className="text-xl font-bold text-stone-900">Create New Ticket</h2>
          <button
            onClick={onClose}
            data-testid="close-create-ticket-modal"
            className="p-2 hover:bg-stone-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-stone-500" strokeWidth={1.5} />
          </button>
        </div>

        {/* Form */}
        <form 
          onSubmit={handleSubmit} 
          onKeyDown={(e) => {
            // Prevent form submission on Enter when in textarea
            if (e.key === 'Enter' && e.target.tagName === 'TEXTAREA') {
              e.stopPropagation();
            }
          }}
          className="p-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Title *</label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter ticket title"
              data-testid="ticket-title-input"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              onKeyDown={(e) => {
                // Prevent form submission on Enter - allow new lines
                if (e.key === 'Enter') {
                  e.stopPropagation();
                }
              }}
              placeholder="Enter ticket description (press Enter for new line)"
              data-testid="ticket-description-input"
              rows={4}
              className="whitespace-pre-wrap"
            />
          </div>

          {/* File Upload Section */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Attachments (Images, PDFs, Videos)
            </label>
            <div className="space-y-3">
              {/* Upload Button */}
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/*,application/pdf,video/*"
                  onChange={handleFileUpload}
                  disabled={uploadingFiles}
                  className="hidden"
                  id="file-upload-input"
                  multiple
                />
                <label htmlFor="file-upload-input">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={uploadingFiles}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('file-upload-input').click();
                    }}
                    data-testid="upload-files-button"
                    className="bg-white border border-stone-200 hover:bg-stone-50 text-stone-700"
                  >
                    <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {uploadingFiles ? 'Uploading...' : 'Upload Files'}
                  </Button>
                </label>
                <span className="text-xs text-stone-500">Max 50MB per file</span>
              </div>

              {/* Uploaded Files Preview */}
              {uploadedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {uploadedFiles.map((file) => {
                    const FileIcon = getFileIcon(file.type);
                    return (
                      <div
                        key={file.id}
                        className="relative group bg-stone-100 rounded-lg p-2 border border-stone-200"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-stone-200 rounded flex items-center justify-center">
                            <FileIcon className="w-5 h-5 text-stone-600" strokeWidth={1.5} />
                          </div>
                          <div className="max-w-[100px]">
                            <p className="text-xs font-medium text-stone-700 truncate">{file.name}</p>
                            <p className="text-xs text-stone-500">{getFileLabel(file.type)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(file.id)}
                            className="p-1 hover:bg-rose-100 rounded transition-colors"
                            data-testid={`remove-file-${file.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-rose-600" strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Board *</label>
              <Select
                value={formData.board_id}
                onValueChange={(value) => setFormData({ ...formData, board_id: value, column_id: '' })}
              >
                <SelectTrigger data-testid="board-select">
                  <SelectValue placeholder="Select board" />
                </SelectTrigger>
                <SelectContent>
                  {boards.map((board) => (
                    <SelectItem key={board.board_id} value={board.board_id}>
                      {board.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Column *</label>
              <Select
                value={formData.column_id}
                onValueChange={(value) => setFormData({ ...formData, column_id: value })}
                disabled={!formData.board_id}
              >
                <SelectTrigger data-testid="column-select">
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  {columns.map((column) => (
                    <SelectItem key={column.column_id} value={column.column_id}>
                      {column.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Priority</label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger data-testid="priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Assignee *</label>
              <Select
                value={formData.assignee_user_id}
                onValueChange={(value) => setFormData({ ...formData, assignee_user_id: value })}
              >
                <SelectTrigger data-testid="assignee-select">
                  <SelectValue placeholder="Select assignee" />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 pb-2 sticky top-0 bg-white">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-stone-400" />
                      <input
                        className="w-full rounded-md border border-stone-200 bg-stone-50 py-2 pl-8 pr-3 text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        placeholder="Search by name or email..."
                        value={assigneeSearch}
                        onChange={(e) => setAssigneeSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        data-testid="assignee-search-input"
                      />
                    </div>
                  </div>
                  {filteredUsers.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.name}
                    </SelectItem>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div className="px-3 py-2 text-xs text-stone-400">No users found</div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Due Date *</label>
            <Input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              data-testid="due-date-input"
            />
          </div>

          {/* Custom Fields */}
          {customFields.length > 0 && (
            <div className="space-y-4 pt-2 border-t border-stone-200">
              <h3 className="text-sm font-semibold text-stone-800">Additional Fields</h3>
              {customFields.map((field) => (
                <div key={field.field_id}>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    {field.name} {field.is_mandatory && <span className="text-rose-500">*</span>}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {field.options.map((option) => (
                      <label
                        key={option.value}
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                          (formData.custom_fields[field.field_id] || []).includes(option.value)
                            ? 'bg-primary/10 border-primary text-primary'
                            : 'bg-white border-stone-200 hover:bg-stone-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={(formData.custom_fields[field.field_id] || []).includes(option.value)}
                          onChange={(e) => handleCustomFieldChange(field.field_id, option.value, e.target.checked)}
                          className="sr-only"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              data-testid="cancel-create-ticket"
              className="bg-white border border-stone-200 hover:bg-stone-50 text-stone-700 rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || uploadingFiles}
              data-testid="submit-create-ticket"
              className="bg-primary hover:bg-primary-hover text-stone-900 rounded-xl shadow-gold font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : uploadingFiles ? 'Uploading files...' : 'Create Ticket'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateTicketModal;
